import { AppFilterType, filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import {
  EmptyStateBase,
  Page,
  PaginationBlock,
  useModals,
  Button,
  FilterRenderType,
  FilterValueType,
  Stack,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { GoalsGroupingByQuarter } from "@/pages/talent/goals/components/GoalsGroupingByQuarter.tsx";
import { GoalsTable } from "@/pages/talent/goals/components/GoalsTable.tsx";
import { useGoalsTableHook } from "@/pages/talent/goals/hooks/use-goals-table.hook.ts";
import { useGoalsSearch, useGoalsStrategicSearch } from "@/queries/goals.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiGoalOwnerType, ApiGoalSearchFilter, ApiGoalStatus } from "@/types/goals.types.ts";
import { useGoalsCustomFilters } from "@/pages/talent/goals/widgets/GoalsCustomFilters.tsx";
import { ApiPermission } from "@/types/role.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { GoalPaneLayout } from "@/pages/talent/goals/components/GoalPaneLayout.tsx";
import { useGoalSidePanel } from "@/pages/talent/goals/hooks/use-goal-side-panel.hook.ts";

export function StrategicGoalsPage() {
  const { t } = useTranslation();
  const modalsService = useModals();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canIEdit } = usePermissions();
  const { openGoalPanel } = useGoalSidePanel();

  const library = filtersLibrary();

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiGoalSearchFilter>({
    filters: [
      library.page_number,
      library.text,
      {
        name: "assignee_ids",
        type: AppFilterType.employee,
        valueType: FilterValueType.numberArray,
        renderType: FilterRenderType.custom,
        label: t("goals:assignees"),
      },
    ],
    source: "strategic-goals",
  });

  const { filterAdditions, renderFilters } = useGoalsCustomFilters();

  const isShowingActive = (searchParams.get("goal_status") || "open") === "open";

  const strategicFilter = useMemo<ApiGoalSearchFilter>(() => {
    return {
      ...filter,
      ...filterAdditions,
    };
  }, [filter, filterAdditions]);

  const filterWithCompany = useMemo<ApiGoalSearchFilter>(() => {
    return {
      ...filter,
      ...filterAdditions,
      owner_type: [ApiGoalOwnerType.company],
      status: [ApiGoalStatus.completed, ApiGoalStatus.failed, ApiGoalStatus.cancelled],
      only_top_level: true,
    };
  }, [filter, filterAdditions]);

  const { data: strategicGoals, isLoading: isStrategicLoading } = useGoalsStrategicSearch(
    strategicFilter,
    isShowingActive
  );
  const { data: closedGoalsData, isLoading: isClosedLoading } = useGoalsSearch(
    filterWithCompany,
    !isShowingActive
  );
  const reversedClosedGoals = useMemo(
    () => [...(closedGoalsData?.items ?? [])].reverse(),
    [closedGoalsData?.items]
  );

  const {
    flatGoals,
    expandedPaths,
    loadingPaths,
    toggleExpand,
    handleEditGoal,
    handleDetailsGoal,
    handleDeleteGoal,
  } = useGoalsTableHook(reversedClosedGoals);

  const handleCreateGoal = () => {
    modalsService.openModal(GoalEditModal, {});
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.goals.listShown);
  });

  useTitle(t("goals:strategic_goals"));

  return (
    <GoalPaneLayout>
      <Page>
        <Page.Header title={t("goals:strategic_goals")}>
          <Stack direction={"horizontal"} gap={"sm"}>
            {renderFilters()}
            {filterButton()}
            {canIEdit(ApiPermission.goals_company_manage) && (
              <Button variant={"primary"} onClick={handleCreateGoal}>
                {t("goals:create_goal")}
              </Button>
            )}
          </Stack>
        </Page.Header>
        <Page.Content>
          <Stack gap={"md"}>
            {isShowingActive ? (
              <GoalsGroupingByQuarter
                goals={strategicGoals ?? []}
                reverse
                isLoading={isStrategicLoading}
                emptyStateComponent={
                  <EmptyStateBase
                    emoji={":dart:"}
                    title={t("goals:goals_empty_state_title")}
                    description={t("goals:goals_empty_state_description")}
                    primaryButton={
                      canIEdit(ApiPermission.goals_company_manage) && (
                        <Button onClick={handleCreateGoal}>{t("goals:create_goal")}</Button>
                      )
                    }
                  />
                }
              />
            ) : (
              <GoalsTable
                flatGoals={flatGoals}
                isLoading={isClosedLoading}
                expandedPaths={expandedPaths}
                loadingPaths={loadingPaths}
                toggleExpand={toggleExpand}
                handleEditGoal={handleEditGoal}
                handleDetailsGoal={handleDetailsGoal}
                handleDeleteGoal={handleDeleteGoal}
                onItemClick={openGoalPanel}
                emptyStateComponent={
                  <EmptyStateBase
                    emoji={":white_check_mark:"}
                    title={t("goals:goals_empty_state_title")}
                    description={t("goals:goals_empty_state_description")}
                  />
                }
              />
            )}

            {!isShowingActive && closedGoalsData && (
              <PaginationBlock
                metadata={closedGoalsData.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            )}
          </Stack>
        </Page.Content>
      </Page>
    </GoalPaneLayout>
  );
}
